export type AttemptStatus = "correct" | "incorrect" | "skipped";

// The only type we ever write to localStorage. Everything else is derived.
export type RawAttempt = { timestamp: number; status: AttemptStatus };

export type Tense =
  | "present"
  | "preterite"
  | "imperfect"
  | "future"
  | "conditional"
  | "present_subjunctive"
  | "imperfect_subjunctive"
  | "present_perfect"
  | "pluperfect";

export type ProgressEvent = {
  timestamp: number;
  infinitive: string;
  tense: Tense;
  pronoun: string;
  status: AttemptStatus;
};

// Runtime types — history is the source of truth; the numeric fields are derived at read time.
export type ConjugationStats = {
  history: RawAttempt[];
  seenCount: number;
  correctCount: number;
  incorrectCount: number;
  skippedCount: number;
  lastSeen: number;
};

export type VerbStats = {
  history: RawAttempt[];
  attempts: number;
  correct: number;
  incorrect: number;
  skipped: number;
  lastSeen: number;
};

// Skill categories whose practice we measure directly (the language modalities).
export type SkillCat = "reading" | "writing" | "speaking" | "listening";

export type ProgressStore = {
  byConjugation: Record<string, ConjugationStats>;
  byVerb: Record<string, VerbStats>;         // derived from byConjugation at read time; never stored
  vocabularyByTerm: Record<string, VerbStats>;
  bySkill: Partial<Record<SkillCat, VerbStats>>;
};

const STORAGE_KEY = "conjugr8.progress.v1";

// Per-key event caps. Raise these to keep more history; the cost is localStorage space.
// Each RawAttempt ≈ 45 bytes in JSON. 500 events × 30 conj-keys = ~675 KB.
const CONJ_EVENT_LIMIT = 500;
const VOCAB_EVENT_LIMIT = 500;
const SKILL_EVENT_LIMIT = 5000; // few keys, so high limit is fine

function getInitialStore(): ProgressStore {
  return { byConjugation: {}, byVerb: {}, vocabularyByTerm: {}, bySkill: {} };
}

function makeKey(infinitive: string, tense: Tense, pronoun: ProgressEvent["pronoun"]): string {
  return `${infinitive}|${tense}|${pronoun}`;
}

// Derive a ConjugationStats from its raw event log.
function hydrateConj(events: RawAttempt[]): ConjugationStats {
  let correct = 0, incorrect = 0, skipped = 0, lastSeen = 0;
  for (const e of events) {
    if (e.status === "correct") correct++;
    else if (e.status === "incorrect") incorrect++;
    else skipped++;
    if (e.timestamp > lastSeen) lastSeen = e.timestamp;
  }
  return { history: events, seenCount: events.length, correctCount: correct, incorrectCount: incorrect, skippedCount: skipped, lastSeen };
}

// Derive a VerbStats from its raw event log.
function hydrateVerb(events: RawAttempt[]): VerbStats {
  let correct = 0, incorrect = 0, skipped = 0, lastSeen = 0;
  for (const e of events) {
    if (e.status === "correct") correct++;
    else if (e.status === "incorrect") incorrect++;
    else skipped++;
    if (e.timestamp > lastSeen) lastSeen = e.timestamp;
  }
  return { history: events, attempts: events.length, correct, incorrect, skipped, lastSeen };
}

// Extract raw events from either the new format (array) or the old format (object with .history).
function extractEvents(v: unknown): RawAttempt[] {
  if (Array.isArray(v)) return v as RawAttempt[];
  if (v && typeof v === "object" && Array.isArray((v as any).history)) return (v as any).history as RawAttempt[];
  return [];
}

export function readProgress(): ProgressStore {
  if (typeof window === "undefined") return getInitialStore();
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return getInitialStore();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return getInitialStore();

    // Hydrate byConjugation from raw event arrays (migrate old object format transparently).
    const byConjugation: Record<string, ConjugationStats> = {};
    for (const [k, v] of Object.entries(parsed.byConjugation ?? {})) {
      byConjugation[k] = hydrateConj(extractEvents(v));
    }

    // Derive byVerb by aggregating conjugation stats per infinitive — never stored separately.
    const byVerb: Record<string, VerbStats> = {};
    for (const [key, conj] of Object.entries(byConjugation)) {
      const inf = key.split("|")[0];
      const acc = byVerb[inf];
      if (!acc) {
        byVerb[inf] = { history: [], attempts: conj.seenCount, correct: conj.correctCount, incorrect: conj.incorrectCount, skipped: conj.skippedCount, lastSeen: conj.lastSeen };
      } else {
        acc.attempts += conj.seenCount;
        acc.correct += conj.correctCount;
        acc.incorrect += conj.incorrectCount;
        acc.skipped += conj.skippedCount;
        if (conj.lastSeen > acc.lastSeen) acc.lastSeen = conj.lastSeen;
      }
    }

    // Hydrate vocabularyByTerm.
    const vocabularyByTerm: Record<string, VerbStats> = {};
    for (const [k, v] of Object.entries(parsed.vocabularyByTerm ?? {})) {
      vocabularyByTerm[k] = hydrateVerb(extractEvents(v));
    }

    // Hydrate bySkill.
    const bySkill: Partial<Record<SkillCat, VerbStats>> = {};
    for (const [k, v] of Object.entries(parsed.bySkill ?? {})) {
      bySkill[k as SkillCat] = hydrateVerb(extractEvents(v));
    }

    return { byConjugation, byVerb, vocabularyByTerm, bySkill };
  } catch {
    return getInitialStore();
  }
}

// Persist only raw event arrays — no derived totals.
function saveProgress(store: ProgressStore): void {
  if (typeof window === "undefined") return;
  try {
    const payload = {
      byConjugation: Object.fromEntries(Object.entries(store.byConjugation).map(([k, v]) => [k, v.history])),
      vocabularyByTerm: Object.fromEntries(Object.entries(store.vocabularyByTerm).map(([k, v]) => [k, v.history])),
      bySkill: Object.fromEntries(
        (Object.entries(store.bySkill) as [SkillCat, VerbStats | undefined][])
          .filter((pair): pair is [SkillCat, VerbStats] => pair[1] != null)
          .map(([k, v]) => [k, v.history])
      ),
      // byVerb is intentionally omitted — it is derived from byConjugation on every read.
    };
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
    window.dispatchEvent(new Event('c8:progress-saved'));
  } catch {}
}

export function clearProgress(): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {}
}

export function recordAttempt(event: ProgressEvent): void {
  const store = readProgress();
  const key = makeKey(event.infinitive, event.tense, event.pronoun);
  const conj = store.byConjugation[key] ?? hydrateConj([]);
  const events = [...conj.history, { timestamp: event.timestamp, status: event.status }];
  store.byConjugation[key] = hydrateConj(events.length > CONJ_EVENT_LIMIT ? events.slice(-CONJ_EVENT_LIMIT) : events);
  saveProgress(store);
}

export function computeVerbLeaderboard(minAttempts = 3): Array<{
  infinitive: string;
  attempts: number;
  accuracy: number;
}> {
  const store = readProgress();
  const entries = Object.entries(store.byVerb).map(([infinitive, s]) => ({
    infinitive,
    attempts: s.attempts,
    accuracy: s.attempts > 0 ? Math.round((s.correct / s.attempts) * 100) : 0,
  }));
  return entries
    .filter((e) => e.attempts >= minAttempts)
    .sort((a, b) => a.accuracy - b.accuracy || b.attempts - a.attempts);
}

export function overallSummary(): { attempts: number; accuracy: number } {
  const store = readProgress();
  let attempts = 0;
  let correct = 0;
  for (const s of Object.values(store.byVerb)) {
    attempts += s.attempts;
    correct += s.correct;
  }
  const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 0;
  try {
    console.groupCollapsed("[Progress] overallSummary");
    console.log({ verbsTracked: Object.keys(store.byVerb).length, attempts, correct, accuracy });
    console.groupEnd();
  } catch {}
  return { attempts, accuracy };
}

// Vocabulary tracking
export type VocabularyEvent = {
  timestamp: number;
  term: string;
  status: AttemptStatus; // correct = "I know it", incorrect = "I don't know it", skipped = "skip"
  meaning?: string; // English translation, stored in sidecar metadata
};

const VOCAB_META_KEY = 'conjugr8.vocab.meta.v1';

export function getVocabMeta(): Record<string, string> {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(VOCAB_META_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch { return {}; }
}

function setVocabMeta(term: string, meaning: string): void {
  if (typeof window === 'undefined') return;
  try {
    const meta = getVocabMeta();
    meta[term] = meaning;
    window.localStorage.setItem(VOCAB_META_KEY, JSON.stringify(meta));
  } catch {}
}

export type VocabState = "unseen" | "evaluating" | "known" | "to_learn";

const VOCAB_KNOWN_WINDOW = 10; // evaluate accuracy over last N attempts
const VOCAB_KNOWN_THRESHOLD = 85; // percent

function getLastNAttempts(stats: VerbStats | undefined, n: number): AttemptStatus[] {
  if (!stats || !stats.history || stats.history.length === 0) return [];
  const slice = stats.history.slice(-n);
  return slice.map((h) => h.status);
}

export function classifyVocabTerm(stats: VerbStats | undefined): {
  state: VocabState;
  attempts: number;
  correct: number;
  incorrect: number;
  windowAttempts: number;
  windowAccuracyPct: number; // 0..100
} {
  if (!stats || stats.attempts === 0) {
    return { state: "unseen", attempts: 0, correct: 0, incorrect: 0, windowAttempts: 0, windowAccuracyPct: 0 };
  }
  const attempts = stats.attempts;
  const correct = stats.correct;
  const incorrect = stats.incorrect;
  // 3/3 immediate known rule
  if (attempts >= 3 && correct >= 3 && incorrect === 0) {
    return { state: "known", attempts, correct, incorrect, windowAttempts: 3, windowAccuracyPct: 100 };
  }
  if (attempts < 3) {
    return { state: "evaluating", attempts, correct, incorrect, windowAttempts: attempts, windowAccuracyPct: Math.round((correct / Math.max(1, attempts)) * 100) };
  }
  // attempts >= 3
  if (incorrect > 0) {
    const lastStatuses = getLastNAttempts(stats, VOCAB_KNOWN_WINDOW);
    const considered = lastStatuses.filter((s) => s !== "skipped");
    const windowAttempts = considered.length;
    const windowCorrect = considered.filter((s) => s === "correct").length;
    const windowAccuracyPct = windowAttempts > 0 ? Math.round((windowCorrect / windowAttempts) * 100) : 0;
    if (windowAttempts >= 3 && windowAccuracyPct >= VOCAB_KNOWN_THRESHOLD) {
      return { state: "known", attempts, correct, incorrect, windowAttempts, windowAccuracyPct };
    }
    return { state: "to_learn", attempts, correct, incorrect, windowAttempts, windowAccuracyPct };
  }
  // fallback
  return { state: "evaluating", attempts, correct, incorrect, windowAttempts: Math.min(attempts, VOCAB_KNOWN_WINDOW), windowAccuracyPct: Math.round((correct / Math.max(1, attempts)) * 100) };
}

export function recordVocabularyAttempt(event: VocabularyEvent): void {
  const store = readProgress();
  const existing = store.vocabularyByTerm[event.term] ?? hydrateVerb([]);
  const events = [...existing.history, { timestamp: event.timestamp, status: event.status }];
  store.vocabularyByTerm[event.term] = hydrateVerb(events.length > VOCAB_EVENT_LIMIT ? events.slice(-VOCAB_EVENT_LIMIT) : events);
  saveProgress(store);
  if (event.meaning) setVocabMeta(event.term, event.meaning);
}

// ---------------------
// Skill (modality) attempts — reading / writing / speaking / listening
// ---------------------
export function recordSkillAttempt(event: { timestamp: number; category: SkillCat; status: AttemptStatus }): void {
  const store = readProgress();
  const existing = store.bySkill[event.category] ?? hydrateVerb([]);
  const events = [...existing.history, { timestamp: event.timestamp, status: event.status }];
  store.bySkill[event.category] = hydrateVerb(events.length > SKILL_EVENT_LIMIT ? events.slice(-SKILL_EVENT_LIMIT) : events);
  saveProgress(store);
}

// Record a whole session at once (e.g. a reading quiz of N questions, M correct).
export function recordSkillBatch(category: SkillCat, correct: number, total: number, timestamp = Date.now()): void {
  if (total <= 0) return;
  const store = readProgress();
  const existing = store.bySkill[category] ?? hydrateVerb([]);
  const incorrect = Math.max(0, total - correct);
  const newEvents: RawAttempt[] = [
    ...Array.from({ length: correct }, () => ({ timestamp, status: "correct" as AttemptStatus })),
    ...Array.from({ length: incorrect }, () => ({ timestamp, status: "incorrect" as AttemptStatus })),
  ];
  const events = [...existing.history, ...newEvents];
  store.bySkill[category] = hydrateVerb(events.length > SKILL_EVENT_LIMIT ? events.slice(-SKILL_EVENT_LIMIT) : events);
  saveProgress(store);
}

export function vocabularyLeaderboard(minAttempts = 1): Array<{
  term: string;
  attempts: number;
  accuracy: number;
}> {
  const store = readProgress();
  const entries = Object.entries(store.vocabularyByTerm).map(([term, s]) => ({
    term,
    attempts: s.attempts,
    accuracy: s.attempts > 0 ? Math.round((s.correct / s.attempts) * 100) : 0,
  }));
  return entries
    .filter((e) => e.attempts >= minAttempts)
    .sort((a, b) => a.accuracy - b.accuracy || b.attempts - a.attempts);
}

// Combined familiarity across verbs (byVerb) and vocabulary (vocabularyByTerm)
export function computeFamiliarityByWord(minAttempts = 1): Array<{
  word: string;
  attempts: number;
  accuracy: number;
  score: number; // 0..100 familiarity
  type: "verb" | "vocab";
}> {
  const store = readProgress();
  const results: Array<{ word: string; attempts: number; accuracy: number; score: number; type: "verb" | "vocab" }> = [];
  for (const [infinitive, s] of Object.entries(store.byVerb)) {
    const attempts = s.attempts;
    const accuracy = attempts > 0 ? (s.correct / attempts) * 100 : 0;
    const saturation = Math.min(1, attempts / 8); // saturate around ~8 attempts
    const score = Math.round(accuracy * saturation);
    results.push({ word: infinitive, attempts, accuracy: Math.round(accuracy), score, type: "verb" });
  }
  for (const [term, s] of Object.entries(store.vocabularyByTerm)) {
    const attempts = s.attempts;
    const accuracy = attempts > 0 ? (s.correct / attempts) * 100 : 0;
    const saturation = Math.min(1, attempts / 8);
    const score = Math.round(accuracy * saturation);
    results.push({ word: term, attempts, accuracy: Math.round(accuracy), score, type: "vocab" });
  }
  return results
    .filter((e) => e.attempts >= minAttempts)
    .sort((a, b) => b.score - a.score || b.attempts - a.attempts || b.accuracy - a.accuracy);
}

export function computeAccuracyByTense(): Array<{
  tense: Tense;
  attempts: number;
  accuracy: number;
}> {
  const store = readProgress();
  const agg: Record<Tense, { attempts: number; correct: number }> = {
    present: { attempts: 0, correct: 0 },
    preterite: { attempts: 0, correct: 0 },
    imperfect: { attempts: 0, correct: 0 },
    future: { attempts: 0, correct: 0 },
    conditional: { attempts: 0, correct: 0 },
    present_subjunctive: { attempts: 0, correct: 0 },
    imperfect_subjunctive: { attempts: 0, correct: 0 },
    present_perfect: { attempts: 0, correct: 0 },
    pluperfect: { attempts: 0, correct: 0 },
  };
  for (const [key, s] of Object.entries(store.byConjugation)) {
    const parts = key.split("|");
    const tense = parts[1] as Tense;
    const attempts = s.seenCount;
    const correct = s.correctCount;
    const entry = agg[tense];
    entry.attempts += attempts;
    entry.correct += correct;
  }
  const result: Array<{ tense: Tense; attempts: number; accuracy: number }> = [];
  (Object.keys(agg) as Tense[]).forEach((tense) => {
    const { attempts, correct } = agg[tense];
    const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 0;
    result.push({ tense, attempts, accuracy });
  });
  return result.sort((a, b) => a.attempts - b.attempts || a.tense.localeCompare(b.tense));
}

export type EstimatedLevel = {
  label: "A1" | "A2" | "B1" | "B2" | "C1" | "C2" | "Native";
  progressToNext: number; // 0..100
  score: number; // raw score
};

export function estimateLevel(): EstimatedLevel {
  const store = readProgress();
  let wordsKnown = 0;
  let verbsMastered = 0;
  for (const s of Object.values(store.byVerb)) {
    const attempts = s.attempts;
    const accuracy = attempts > 0 ? (s.correct / attempts) * 100 : 0;
    if (attempts >= 3 && accuracy >= 70) wordsKnown += 1;
    if (attempts >= 5 && accuracy >= 80) verbsMastered += 1;
  }
  for (const s of Object.values(store.vocabularyByTerm)) {
    const attempts = s.attempts;
    const accuracy = attempts > 0 ? (s.correct / attempts) * 100 : 0;
    if (attempts >= 3 && accuracy >= 70) wordsKnown += 1;
  }
  const score = Math.round(wordsKnown + verbsMastered * 1.5);

  const thresholds: Array<{ label: EstimatedLevel["label"]; min: number }> = [
    { label: "A1", min: 0 },
    { label: "A2", min: 50 },
    { label: "B1", min: 120 },
    { label: "B2", min: 250 },
    { label: "C1", min: 400 },
    { label: "C2", min: 600 },
    { label: "Native", min: 900 },
  ];
  let current = thresholds[0];
  let next = thresholds[1];
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (score >= thresholds[i].min) {
      current = thresholds[i];
      next = thresholds[Math.min(i + 1, thresholds.length - 1)];
      break;
    }
  }
  const range = Math.max(1, next.min - current.min);
  const progressToNext = Math.max(0, Math.min(100, Math.round(((score - current.min) / range) * 100)));
  return { label: current.label, progressToNext, score };
}

export function computeGrammarScore(): number {
  const tenses = computeAccuracyByTense();
  const level = estimateLevel().label;
  const weights: Record<EstimatedLevel["label"], Partial<Record<Tense, number>>> = {
    A1: { present: 1 },
    A2: { present: 0.6, preterite: 0.4, imperfect: 0.4, future: 0.4, conditional: 0.2 },
    B1: { present: 0.4, preterite: 0.4, imperfect: 0.4, future: 0.4, conditional: 0.3, present_subjunctive: 0.2 },
    B2: { present: 0.3, preterite: 0.4, imperfect: 0.4, future: 0.3, conditional: 0.3, present_subjunctive: 0.3 },
    C1: { present: 0.2, preterite: 0.3, imperfect: 0.3, future: 0.2, conditional: 0.2, present_subjunctive: 0.5 },
    C2: { present: 0.2, preterite: 0.3, imperfect: 0.3, future: 0.2, conditional: 0.2, present_subjunctive: 0.5 },
    Native: { present: 0.2, preterite: 0.3, imperfect: 0.3, future: 0.2, conditional: 0.2, present_subjunctive: 0.5 },
  } as any;
  const w = weights[level] || weights.A1;
  let sum = 0;
  let denom = 0;
  for (const t of tenses) {
    const weight = (w as any)[t.tense] ?? 0;
    sum += t.accuracy * weight;
    denom += weight * 100;
  }
  const pct = denom > 0 ? Math.round((sum / denom) * 100) : 0;
  return pct;
}

// Enhanced vocabulary progress tracking
export function getVocabularyStats(): {
  totalWords: number;
  studiedWords: number;
  wellKnownWords: number;
  learningWords: number;
  newWords: number;
  averageAccuracy: number;
  totalAttempts: number;
} {
  const store = readProgress();
  const stats = Object.values(store.vocabularyByTerm);
  
  const totalWords = stats.length;
  const studiedWords = stats.filter(s => s.attempts > 0).length;
  const wellKnownWords = stats.filter(s => s.attempts >= 3 && (s.correct / s.attempts) >= 0.8).length;
  const learningWords = stats.filter(s => s.attempts >= 1 && (s.correct / s.attempts) >= 0.4 && (s.correct / s.attempts) < 0.8).length;
  const newWords = stats.filter(s => s.attempts === 0).length;
  
  const totalAttempts = stats.reduce((sum, s) => sum + s.attempts, 0);
  const totalCorrect = stats.reduce((sum, s) => sum + s.correct, 0);
  const averageAccuracy = totalAttempts > 0 ? Math.round((totalCorrect / totalAttempts) * 100) : 0;
  
  return {
    totalWords,
    studiedWords,
    wellKnownWords,
    learningWords,
    newWords,
    averageAccuracy,
    totalAttempts,
  };
}

export function getWordsNeedingReview(maxDaysSinceLastSeen = 7): Array<{
  term: string;
  stats: VerbStats;
  daysSinceLastSeen: number;
  priority: number; // 0-100, higher = more urgent
}> {
  const store = readProgress();
  const now = Date.now();
  const dayMs = 24 * 60 * 60 * 1000;
  
  const words = Object.entries(store.vocabularyByTerm)
    .map(([term, stats]) => {
      const daysSinceLastSeen = Math.floor((now - stats.lastSeen) / dayMs);
      const accuracy = stats.attempts > 0 ? (stats.correct / stats.attempts) : 0;
      
      // Calculate priority based on:
      // - How long since last seen (more days = higher priority)
      // - How well known the word is (lower accuracy = higher priority)
      // - How many attempts (more attempts = lower priority for review)
      const timePriority = Math.min(100, daysSinceLastSeen * 10);
      const accuracyPriority = (1 - accuracy) * 50;
      const attemptPenalty = Math.min(20, stats.attempts * 2);
      const priority = Math.max(0, timePriority + accuracyPriority - attemptPenalty);
      
      return {
        term,
        stats,
        daysSinceLastSeen,
        priority,
      };
    })
    .filter(w => w.daysSinceLastSeen >= maxDaysSinceLastSeen || w.priority > 30)
    .sort((a, b) => b.priority - a.priority);
    
  return words;
}

export function getVocabularyProgressByGroup(): Array<{
  groupId: string;
  groupTitle: string;
  totalWords: number;
  studiedWords: number;
  wellKnownWords: number;
  averageAccuracy: number;
  progress: number; // 0-100
}> {
  // This would need to be enhanced to work with the actual vocab groups
  // For now, return a basic structure
  return [];
}


// ---------------------
// CEFR vocabulary progress
// ---------------------
export type CEFR = "A1" | "A2" | "B1" | "B2" | "C1" | "C2";

type LevelProgress = {
  level: CEFR;
  totalWords: number;
  learnedWords: number;
  studiedWords: number; // attempts > 0
  progressPct: number; // learned / total * 100
};

// Criteria to consider a vocab word "learned"
function isWordLearned(stats: VerbStats | undefined): boolean {
  if (!stats) return false;
  if (stats.attempts < 3) return false;
  const accuracy = stats.correct / Math.max(1, stats.attempts);
  return accuracy >= 0.8;
}

// Get per-CEFR vocabulary for a language from content registry
import { getLanguageContent } from "../content/index.js";

async function getVocabularyByCEFR(languageCode: string): Promise<Record<CEFR, { term: string; meaning: string }[]>> {
  const content = getLanguageContent(languageCode);
  const empty: Record<CEFR, { term: string; meaning: string }[]> = {
    A1: [],
    A2: [],
    B1: [],
    B2: [],
    C1: [],
    C2: [],
  };
  if (!content) return empty;
  
  // Handle async vocabulary loading
  const vocabulary = typeof content.vocabulary === 'function' 
    ? await content.vocabulary() 
    : content.vocabulary;
    
  for (const category of vocabulary) {
    const prefix = category.title.split(" - ")[0] as CEFR;
    if (prefix && (Object.prototype.hasOwnProperty.call(empty, prefix))) {
      (empty as any)[prefix] = category.words;
    }
  }
  return empty;
}

export async function computeVocabProgressByCEFR(languageCode: string): Promise<LevelProgress[]> {
  const store = readProgress();
  const byLevel = await getVocabularyByCEFR(languageCode);
  const levels: CEFR[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
  const results = levels.map((level) => {
    const words = byLevel[level] || [];
    const totalWords = words.length;
    let learnedWords = 0;
    let studiedWords = 0;
    for (const w of words) {
      const stats = store.vocabularyByTerm[w.term];
      if (stats && stats.attempts > 0) studiedWords += 1;
      if (isWordLearned(stats)) learnedWords += 1;
    }
    const progressPct = totalWords > 0 ? Math.round((learnedWords / totalWords) * 100) : 0;
    return { level, totalWords, learnedWords, studiedWords, progressPct };
  });
  try {
    console.groupCollapsed("[Vocab CEFR] computeVocabProgressByCEFR", languageCode);
    for (const r of results) {
      console.log(r.level, { totalWords: r.totalWords, studiedWords: r.studiedWords, learnedWords: r.learnedWords, progressPct: r.progressPct });
    }
    console.groupEnd();
  } catch {}
  return results;
}

export async function computeVocabStateCountsByCEFR(languageCode: string): Promise<Array<{
  level: CEFR;
  counts: { unseen: number; evaluating: number; to_learn: number; known: number };
}>> {
  const store = readProgress();
  const byLevel = await getVocabularyByCEFR(languageCode);
  const levels: CEFR[] = ["A1", "A2", "B1", "B2", "C1", "C2"];
  return levels.map((level) => {
    const words = byLevel[level] || [];
    let unseen = 0;
    let evaluating = 0;
    let to_learn = 0;
    let known = 0;
    for (const w of words) {
      const stats = store.vocabularyByTerm[w.term];
      const c = classifyVocabTerm(stats);
      if (c.state === "unseen") unseen += 1;
      else if (c.state === "evaluating") evaluating += 1;
      else if (c.state === "to_learn") to_learn += 1;
      else if (c.state === "known") known += 1;
    }
    return { level, counts: { unseen, evaluating, to_learn, known } };
  });
}

export async function computeVocabOverallProgress(languageCode: string): Promise<{
  levels: LevelProgress[];
  completedLevels: number;
  vocabProgressPct: number; // percent of vocab track complete via CEFR completion
  partialVocabProgressPct: number; // percent if we allow partial credit per level
}> {
  const levels = await computeVocabProgressByCEFR(languageCode);
  const completedLevels = levels.filter((l) => l.totalWords > 0 && l.learnedWords >= l.totalWords).length;
  const vocabProgressPct = Math.round((completedLevels / 6) * 100);
  const partialFraction = levels.reduce((sum, l) => sum + (l.totalWords > 0 ? l.learnedWords / l.totalWords : 0), 0) / 6;
  const partialVocabProgressPct = Math.round(partialFraction * 100);
  try {
    console.groupCollapsed("[Vocab CEFR] computeVocabOverallProgress", languageCode);
    console.log({ completedLevels, vocabProgressPct, partialFraction, partialVocabProgressPct });
    console.table(levels.map(l => ({ level: l.level, total: l.totalWords, studied: l.studiedWords, learned: l.learnedWords, pct: l.progressPct })));
    console.groupEnd();
  } catch {}
  return { levels, completedLevels, vocabProgressPct, partialVocabProgressPct };
}

// Total progress contribution from vocabulary assuming vocab = 25% of total
// Each CEFR level fully learned contributes 1/24 to total (≈4.1667%)
export async function computeTotalProgressContribution(languageCode: string): Promise<{
  completedVocabLevels: number;
  totalContributionPct: number; // whole-level credit only
  partialTotalContributionPct: number; // proportional credit across levels
}> {
  const { levels, completedLevels } = await computeVocabOverallProgress(languageCode);
  const totalContributionPct = Math.round((completedLevels / 24) * 100);
  const partialFraction = levels.reduce((sum, l) => sum + (l.totalWords > 0 ? l.learnedWords / l.totalWords : 0), 0) / 24;
  const partialTotalContributionPct = Math.round(partialFraction * 100);
  try {
    console.groupCollapsed("[Vocab CEFR] computeTotalProgressContribution", languageCode);
    console.log({ completedLevels, totalContributionPct, partialFraction, partialTotalContributionPct });
    console.groupEnd();
  } catch {}
  return { completedVocabLevels: completedLevels, totalContributionPct, partialTotalContributionPct };
}

// ---------------------
// Derived CEFR level from four-track progress with gating
// ---------------------
export function deriveLevelFromTracks(input: {
  vocab: number; // 0..100
  grammar: number; // 0..100
  writing: number; // 0..100
  comprehension: number; // 0..100
  overall: number; // average of four
}): { label: CEFR; progressToNext: number } {
  const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));
  const vocab = clamp(input.vocab);
  const grammar = clamp(input.grammar);
  const writing = clamp(input.writing);
  const comprehension = clamp(input.comprehension);
  const overall = clamp(input.overall);

  const thresholds: Array<{ label: CEFR; min: number }> = [
    { label: "A1", min: 0 },
    { label: "A2", min: 20 },
    { label: "B1", min: 40 },
    { label: "B2", min: 60 },
    { label: "C1", min: 80 },
    { label: "C2", min: 95 },
  ];

  // Base level from overall progress
  let baseIndex = 0;
  for (let i = thresholds.length - 1; i >= 0; i--) {
    if (overall >= thresholds[i].min) { baseIndex = i; break; }
  }

  // Gating caps: require minima on key tracks to unlock higher bands
  // - To leave A1: need vocab>=20 and grammar>=20
  // - To reach B1: need vocab>=40 and grammar>=40 and writing>=20
  // - To reach B2+: also require comprehension>=40 and writing>=40 progressively
  let capIndex = thresholds.length - 1;
  if (vocab < 20 || grammar < 20) capIndex = Math.min(capIndex, 0); // A1 max
  else if (vocab < 40 || grammar < 40) capIndex = Math.min(capIndex, 1); // A2 max
  else if (writing < 20) capIndex = Math.min(capIndex, 2); // B1 max
  else if (comprehension < 40 || writing < 40) capIndex = Math.min(capIndex, 3); // B2 max

  const finalIndex = Math.min(baseIndex, capIndex);
  const current = thresholds[finalIndex];
  const next = thresholds[Math.min(finalIndex + 1, thresholds.length - 1)];
  const range = Math.max(1, next.min - current.min);
  const progressToNext = Math.max(0, Math.min(100, Math.round(((overall - current.min) / range) * 100)));

  try {
    console.groupCollapsed("[Level] deriveLevelFromTracks");
    console.log({ overall, vocab, grammar, writing, comprehension, base: thresholds[baseIndex].label, cap: thresholds[capIndex].label, final: current.label, progressToNext });
    console.groupEnd();
  } catch {}

  return { label: current.label, progressToNext };
}

// ---------------------
// Grammar progress (recent accuracy per CEFR tenses)
// ---------------------
export function computeRecentAccuracyByTense(maxHistoryPerConjugation: number = 5): Array<{
  tense: Tense;
  recentAttempts: number;
  recentCorrect: number;
  recentAccuracy: number; // 0..100
}> {
  const store = readProgress();
  const agg: Record<Tense, { attempts: number; correct: number }> = {
    present: { attempts: 0, correct: 0 },
    preterite: { attempts: 0, correct: 0 },
    imperfect: { attempts: 0, correct: 0 },
    future: { attempts: 0, correct: 0 },
    conditional: { attempts: 0, correct: 0 },
    present_subjunctive: { attempts: 0, correct: 0 },
    imperfect_subjunctive: { attempts: 0, correct: 0 },
    present_perfect: { attempts: 0, correct: 0 },
    pluperfect: { attempts: 0, correct: 0 },
  };
  for (const [key, s] of Object.entries(store.byConjugation)) {
    const [, tenseRaw] = key.split("|");
    const tense = tenseRaw as Tense;
    const recent = (s.history || []).slice(-maxHistoryPerConjugation);
    let attempts = 0;
    let correct = 0;
    for (const h of recent) {
      attempts += 1;
      if (h.status === "correct") correct += 1;
    }
    agg[tense].attempts += attempts;
    agg[tense].correct += correct;
  }
  const result: Array<{ tense: Tense; recentAttempts: number; recentCorrect: number; recentAccuracy: number }> = [];
  (Object.keys(agg) as Tense[]).forEach((tense) => {
    const { attempts, correct } = agg[tense];
    const accuracy = attempts > 0 ? Math.round((correct / attempts) * 100) : 0;
    result.push({ tense, recentAttempts: attempts, recentCorrect: correct, recentAccuracy: accuracy });
  });
  try {
    console.groupCollapsed("[Grammar] computeRecentAccuracyByTense");
    console.table(result.map(r => ({ tense: r.tense, attempts: r.recentAttempts, correct: r.recentCorrect, acc: r.recentAccuracy })));
    console.groupEnd();
  } catch {}
  return result;
}

function tensesForLevel(label: CEFR): Tense[] {
  // Spanish mapping; extend as more tenses are added
  if (label === "A1") return ["present"];
  if (label === "A2") return ["present", "future", "preterite", "imperfect"];
  if (label === "B1") return ["present", "future", "preterite", "imperfect", "conditional", "present_subjunctive"];
  // B2+ would include more subjunctive/compound tenses when available; for now same as B1
  return ["present", "future", "preterite", "imperfect", "conditional", "present_subjunctive"];
}

export function computeGrammarProgressRecentCEFR(languageCode: string): {
  targetLevel: CEFR;
  tenses: Tense[];
  byTense: Array<{ tense: Tense; recentAttempts: number; recentAccuracy: number }>;
  progressPct: number; // average across target tenses; missing tenses count as 0
} {
  const level = estimateLevel().label as CEFR;
  const targetTenses = tensesForLevel(level);
  const recent = computeRecentAccuracyByTense(5);
  const byTense = targetTenses.map((t) => {
    const entry = recent.find((r) => r.tense === t);
    return { tense: t, recentAttempts: entry?.recentAttempts ?? 0, recentAccuracy: entry?.recentAccuracy ?? 0 };
  });
  const sum = byTense.reduce((s, r) => s + r.recentAccuracy, 0);
  const progressPct = targetTenses.length > 0 ? Math.round(sum / targetTenses.length) : 0;
  try {
    console.groupCollapsed("[Grammar] computeGrammarProgressRecentCEFR", level);
    console.log({ progressPct });
    console.table(byTense.map(b => ({ tense: b.tense, attempts: b.recentAttempts, acc: b.recentAccuracy })));
    console.groupEnd();
  } catch {}
  return { targetLevel: level, tenses: targetTenses, byTense, progressPct };
}

// ---------------------
// Skills — a holistic breakdown of the parts of language learning, each scored
// 0..100 from the data we actually track. Used by the Progress page's radar.
// ---------------------
// The six skill axes — five CEFR competences plus a habit (consistency).
export type SkillId = "vocabulary" | "grammar" | "reading" | "writing" | "speaking" | "consistency";
export type Skill = {
  id: SkillId;
  name: string;
  icon: string;
  score: number;        // 0..100
  tier: string;         // Untouched … Master
  blurb: string;        // what this skill measures
  tip: string;          // how to grow it
  competence: boolean;  // counts toward the CEFR grade?
};

function skillTier(score: number): string {
  if (score >= 90) return "Master";
  if (score >= 70) return "Expert";
  if (score >= 50) return "Adept";
  if (score >= 30) return "Apprentice";
  if (score >= 10) return "Novice";
  return "Untouched";
}

// Smooth saturating curve: count → 0..100, reaching ~63 at `k`, ~86 at 2k.
function saturate(count: number, k: number): number {
  return Math.round(100 * (1 - Math.exp(-Math.max(0, count) / k)));
}

// A modality score from its tracked attempts: accuracy, tempered by volume.
function modalityScore(stats: VerbStats | undefined, k: number): number {
  if (!stats || stats.attempts === 0) return 0;
  const acc = stats.correct / stats.attempts;          // 0..1
  const vol = Math.min(1, stats.attempts / k);
  return Math.round(acc * 100 * (0.55 + 0.45 * vol));
}

export function computeSkills(languageCode: string): Skill[] {
  const store = readProgress();
  const summary = overallSummary();
  const vocab = getVocabularyStats();
  const grammar = computeGrammarProgressRecentCEFR(languageCode);

  // Active practice days within the last 7 days, across every tracked surface.
  const dayMs = 24 * 60 * 60 * 1000;
  const now = Date.now();
  const days = new Set<string>();
  const collect = (h?: { timestamp: number }[]) => {
    for (const e of h ?? []) {
      if (now - e.timestamp < 7 * dayMs) {
        const d = new Date(e.timestamp);
        days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
      }
    }
  };
  for (const s of Object.values(store.byConjugation)) collect(s.history);
  for (const s of Object.values(store.vocabularyByTerm)) collect(s.history);
  for (const s of Object.values(store.bySkill)) collect(s?.history);
  const activeDays = days.size;

  // Grammar = conjugation accuracy (tempered by volume) blended with tense range.
  const conjScore = Math.round(summary.accuracy * Math.min(1, 0.45 + summary.attempts / 320));
  const grammarScore = Math.round(0.6 * conjScore + 0.4 * grammar.progressPct);

  const reading = store.bySkill.reading;
  const writing = store.bySkill.writing;
  const speaking = store.bySkill.speaking;

  const skills: Omit<Skill, "tier">[] = [
    { id: "vocabulary", name: "Vocabulary", icon: "📖", competence: true, score: saturate(vocab.wellKnownWords, 55), blurb: `${vocab.wellKnownWords} words you reliably know.`, tip: "Learn new words in the Vocabulary deck." },
    { id: "grammar", name: "Grammar", icon: "🔀", competence: true, score: grammarScore, blurb: "Forming verbs correctly across tenses & moods.", tip: "Run Drills and Conjugarden expeditions." },
    { id: "reading", name: "Reading", icon: "📕", competence: true, score: modalityScore(reading, 24), blurb: reading?.attempts ? `${reading.attempts} comprehension questions answered.` : "Not measured yet.", tip: "Do Reading comprehension exercises." },
    { id: "writing", name: "Writing", icon: "✍️", competence: true, score: modalityScore(writing, 30), blurb: writing?.attempts ? `${writing.attempts} sentences built.` : "Not measured yet.", tip: "Build sentences in the Sentences practice." },
    { id: "speaking", name: "Speaking", icon: "🗣️", competence: true, score: modalityScore(speaking, 50), blurb: speaking?.attempts ? `${speaking.attempts} phrases drilled.` : "Not measured yet.", tip: "Drill phrases in Speaking practice." },
    { id: "consistency", name: "Consistency", icon: "🔥", competence: false, score: Math.min(100, Math.round((activeDays / 7) * 100)), blurb: `Practised ${activeDays} of the last 7 days.`, tip: "A little practice every day compounds fast." },
  ];

  return skills.map((s) => ({ ...s, tier: skillTier(s.score) }));
}

// ---------------------
// CEFR grade — fuse the five competences into a single, gated CEFR level.
// A spiky profile (e.g. great grammar, zero reading) is capped: you can't reach
// B2 without actually reading, writing and speaking. The weakest competence is
// surfaced so the learner knows what to practise next.
// ---------------------
export type CEFRGrade = {
  label: CEFR;
  progressToNext: number;        // 0..100 within the current band
  overall: number;               // 0..100 weighted competence average
  competences: Skill[];          // the five that count
  weakest: { id: SkillId; name: string; score: number } | null;
  cappedBy: string | null;       // explanation if gating is holding you back
};

export function computeCEFR(languageCode: string): CEFRGrade {
  const skills = computeSkills(languageCode);
  const comp = skills.filter((s) => s.competence);
  const get = (id: SkillId) => comp.find((s) => s.id === id)?.score ?? 0;
  const vocab = get("vocabulary"), grammar = get("grammar"), reading = get("reading"), writing = get("writing"), speaking = get("speaking");

  const weights: Record<string, number> = { vocabulary: 1.2, grammar: 1.4, reading: 1, writing: 1, speaking: 0.9 };
  const wsum = comp.reduce((s, c) => s + (weights[c.id] ?? 1), 0);
  const overall = Math.round(comp.reduce((s, c) => s + c.score * (weights[c.id] ?? 1), 0) / Math.max(1, wsum));

  const thresholds: Array<{ label: CEFR; min: number }> = [
    { label: "A1", min: 0 }, { label: "A2", min: 20 }, { label: "B1", min: 40 },
    { label: "B2", min: 60 }, { label: "C1", min: 80 }, { label: "C2", min: 95 },
  ];
  let baseIndex = 0;
  for (let i = thresholds.length - 1; i >= 0; i--) { if (overall >= thresholds[i].min) { baseIndex = i; break; } }

  const all = [vocab, grammar, reading, writing, speaking];
  const allAtLeast = (n: number) => all.every((v) => v >= n);
  let capIndex = thresholds.length - 1;
  let cappedBy: string | null = null;
  const labelOf = (id: SkillId) => comp.find((s) => s.id === id)?.name ?? id;

  if (vocab < 15 || grammar < 15) { capIndex = 0; cappedBy = "Build core vocabulary and grammar to leave A1."; }
  else if (reading < 12 || writing < 8) { capIndex = 1; cappedBy = `Start ${reading < 12 ? "reading" : "writing"} practice to reach B1.`; }
  else if (speaking < 12 || !allAtLeast(22)) { capIndex = 2; cappedBy = "Round out all skills (especially speaking) to reach B2."; }
  else if (!allAtLeast(50)) { capIndex = 3; cappedBy = "Push every skill past 50 to reach C1."; }
  else if (!allAtLeast(82)) { capIndex = 4; cappedBy = "Push every skill past 82 to reach C2."; }

  const finalIndex = Math.min(baseIndex, capIndex);
  const current = thresholds[finalIndex];
  const next = thresholds[Math.min(finalIndex + 1, thresholds.length - 1)];
  const range = Math.max(1, next.min - current.min);
  const progressToNext = finalIndex >= thresholds.length - 1 ? 100 : Math.max(0, Math.min(100, Math.round(((overall - current.min) / range) * 100)));

  const weakestSkill = [...comp].sort((a, b) => a.score - b.score)[0] ?? null;
  const weakest = weakestSkill ? { id: weakestSkill.id, name: weakestSkill.name, score: weakestSkill.score } : null;
  // Only show the gating note if it's actually below the base band (i.e. holding you back).
  const heldBack = finalIndex < baseIndex ? cappedBy : null;

  return { label: current.label, progressToNext, overall, competences: comp, weakest, cappedBy: heldBack };
}


